import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { DayType } from '../utils/week';
import { useSchedules } from '../context/ScheduleContext';
import { getNotices, getNoticeDetail } from '../services/api';
import type { Schedule } from '../types/schedule';
import type { Notice } from '../types/notice';

type ModalType = {
  year: number;
  month: number;
  date: number;
  day: DayType;
  toggleModal: (year: number, month: number, date: number) => void;
  selectedSchedule?: Schedule;
  isEdit?: boolean;
};

export default function Modal({
  year,
  month,
  date,
  day,
  toggleModal,
  selectedSchedule,
  isEdit = false,
}: ModalType) {
  const formatDate = (year: number, month: number, date: number): string => {
    return `${year}-${month.toString().padStart(2, '0')}-${date.toString().padStart(2, '0')}`;
  };

  const [category, setCategory] = useState<'교역자' | '목자' | '기타'>(
    selectedSchedule?.category || '목자'
  );
  const [content, setContent] = useState<string>(selectedSchedule?.content || '');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isDateRange, setIsDateRange] = useState<boolean>(!!selectedSchedule?.end_date);
  const [endDate, setEndDate] = useState<string>(
    selectedSchedule?.end_date || formatDate(year, month, date)
  );
  const { addSchedule, editSchedule, removeSchedule } = useSchedules();
  const [recentNotice, setRecentNotice] = useState<Notice | null>(null);
  const [showNoticeDetail, setShowNoticeDetail] = useState(false);
  const [noticeDetail, setNoticeDetail] = useState<Notice | null>(null);

  // useEffect(() => {
  //   if (!content && !isEdit) {
  //     setContent('자세히 작성해주세요.');
  //   }
  // }, [content, isEdit]);

  useEffect(() => {
    const fetchRecentNotice = async () => {
      try {
        const response = await getNotices(1, 1);
        if (response.success && response.data.length > 0) {
          setRecentNotice(response.data[0]);
        }
      } catch (error) {
        console.error('Failed to fetch recent notice:', error);
      }
    };

    fetchRecentNotice();
  }, []);

  useEffect(() => {
    setCategory(selectedSchedule?.category || '목자');
  }, [selectedSchedule]);

  const handleSubmit = async () => {
    if (!content.trim()) {
      setError('일정 내용을 입력해주세요.');
      return;
    }

    if (!password) {
      setError('비밀번호를 입력해주세요.');
      return;
    }

    if (!isEdit && password !== confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }

    // 기간 설정 유효성 검사
    const startDateObj = new Date(formatDate(year, month, date));
    const endDateObj = new Date(endDate);

    if (isDateRange && endDateObj < startDateObj) {
      setError('종료일은 시작일 이후여야 합니다.');
      return;
    }

    try {
      if (isEdit && selectedSchedule?.id) {
        const success = await editSchedule(
          selectedSchedule.id,
          content,
          isDateRange ? endDate : null,
          password,
          category
        );
        if (!success) {
          setError('일정 수정에 실패했습니다. 비밀번호를 확인해주세요.');
          return;
        }
      } else {
        const newSchedule: Schedule = {
          date: formatDate(year, month, date),
          end_date: isDateRange ? endDate : null,
          category,
          content,
          password,
        };
        const success = await addSchedule(newSchedule);
        if (!success) {
          setError('일정 추가에 실패했습니다.');
          return;
        }
      }
      toggleModal(year, month, date);
    } catch (err) {
      setError('오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const handleDelete = async () => {
    if (!selectedSchedule?.id) return;

    if (!password) {
      setError('비밀번호를 입력해주세요.');
      return;
    }

    try {
      const success = await removeSchedule(selectedSchedule.id, password);
      if (!success) {
        setError('일정 삭제에 실패했습니다. 비밀번호를 확인해주세요.');
        return;
      }
      toggleModal(year, month, date);
    } catch (err) {
      setError('오류가 발생했습니다. 다시 시도해주세요.');
    }
  };

  const truncateText = (text: string, byteLimit: number = 30): string => {
    const getByteLength = (str: string) => {
      let byte = 0;
      for (let i = 0; i < str.length; i++) {
        str.charCodeAt(i) > 127 ? (byte += 2) : (byte += 1);
      }
      return byte;
    };

    let byte = 0;
    let idx = 0;
    for (let i = 0; i < text.length; i++) {
      text.charCodeAt(i) > 127 ? (byte += 2) : (byte += 1);
      if (byte > byteLimit) {
        idx = i;
        break;
      }
    }

    return idx > 0 ? text.substring(0, idx) + '...' : text;
  };

  const handleNoticeClick = async () => {
    if (!recentNotice?.id) return;

    try {
      const response = await getNoticeDetail(recentNotice.id);
      if (response.success && response.data) {
        setNoticeDetail(response.data);
        setShowNoticeDetail(true);
      }
    } catch (error) {
      console.error('Failed to fetch notice detail:', error);
    }
  };

  return (
    <ModalWrapper>
      <ModalContent>
        <ModalTitle>
          {month}월 {date}일 ({day.toString()})
        </ModalTitle>
        <FormGroup>
          <Label>카테고리</Label>
          <CategorySelect
            value={category}
            onChange={(e) => setCategory(e.target.value as '교역자' | '목자' | '기타')}
          >
            <option value="교역자">교역자</option>
            <option value="목자">목자</option>
            <option value="기타">기타</option>
          </CategorySelect>
        </FormGroup>
        <FormGroup>
          <Label>일정 내용</Label>
          <p style={{ marginBottom: '10px', color: 'gray', fontSize: 'smaller' }}>
            예) 사랑1목장 13:00~15:00 김동안가정 심방
          </p>
          <ModalTextArea
            placeholder="내용을 입력해주세요."
            value={content}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setContent(e.target.value)}
            rows={3}
          />
        </FormGroup>
        <FormGroup>
          <CheckboxContainer>
            <Checkbox
              type="checkbox"
              id="isDateRange"
              checked={isDateRange}
              onChange={(e) => setIsDateRange(e.target.checked)}
            />
            <CheckboxLabel htmlFor="isDateRange">기간 설정</CheckboxLabel>
          </CheckboxContainer>
        </FormGroup>
        {isDateRange && (
          <FormGroup>
            <Label>종료일</Label>
            <DateInput
              type="date"
              value={endDate}
              min={formatDate(year, month, date)}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </FormGroup>
        )}
        <FormGroup>
          <Label>{isEdit ? '비밀번호 확인' : '비밀번호'}</Label>
          <ModalInput
            type="password"
            placeholder={isEdit ? '등록 시 입력한 비밀번호' : '비밀번호를 입력해주세요'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FormGroup>
        {!isEdit && (
          <FormGroup>
            <Label>비밀번호 확인</Label>
            <ModalInput
              type="password"
              placeholder="비밀번호를 다시 입력해주세요"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </FormGroup>
        )}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <ButtonGroup>
          <CancelButton type="button" onClick={() => toggleModal(year, month, date)}>
            취소
          </CancelButton>
          {isEdit && (
            <DeleteButton type="button" onClick={handleDelete}>
              삭제
            </DeleteButton>
          )}
          <SubmitButton type="button" onClick={handleSubmit}>
            {isEdit ? '수정' : '등록'}
          </SubmitButton>
        </ButtonGroup>
        {recentNotice && (
          <NoticeContainer onClick={handleNoticeClick}>
            <NoticeLabel>최근 공지사항</NoticeLabel>
            <NoticeText>{truncateText(recentNotice.title)}</NoticeText>
          </NoticeContainer>
        )}
      </ModalContent>
      {showNoticeDetail && noticeDetail && (
        <NoticeDetailModal>
          <NoticeDetailContent>
            <NoticeDetailHeader>
              <h2>{noticeDetail.title}</h2>
              <CloseButton onClick={() => setShowNoticeDetail(false)}>×</CloseButton>
            </NoticeDetailHeader>
            <NoticeDetailDate>
              {noticeDetail.created_at
                ? new Date(noticeDetail.created_at).toLocaleDateString()
                : ''}
            </NoticeDetailDate>
            <NoticeDetailBody>
              {noticeDetail.content.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </NoticeDetailBody>
          </NoticeDetailContent>
        </NoticeDetailModal>
      )}
    </ModalWrapper>
  );
}

const ModalWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled.div`
  width: 500px;
  padding: 30px;
  border-radius: 8px;
  background-color: #fff;
  margin: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
`;

const ModalTitle = styled.h1`
  font-size: 1.4rem;
  font-weight: 600;
  margin-bottom: 20px;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  font-weight: 500;
`;

const CategorySelect = styled.select`
  width: 100%;
  height: 50px;
  padding: 5px 10px;
  font-size: 1rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;

  &:focus {
    outline: none;
    border-color: #3d85f5;
  }
`;

const ModalInput = styled.input`
  width: 100%;
  height: 50px;
  padding: 5px 10px;
  font-size: 1rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #3d85f5;
  }
`;

const ModalTextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 10px;
  font-size: 1rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;
  font-family: inherit;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #3d85f5;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
`;

const Button = styled.button`
  height: 40px;
  padding: 0 20px;
  border-radius: 4px;
  font-weight: 500;
  cursor: pointer;
`;

const CancelButton = styled(Button)`
  background-color: #f1f1f1;
  color: #333;
  border: 1px solid #ddd;
`;

const SubmitButton = styled(Button)`
  background-color: #3d85f5;
  color: #fff;
  border: none;
`;

const DeleteButton = styled(Button)`
  background-color: #e30747;
  color: #fff;
  border: none;
`;

const ErrorMessage = styled.p`
  color: #e30747;
  margin-top: 10px;
  font-size: 0.9rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: 5px;
`;

const Checkbox = styled.input`
  margin-right: 8px;
  width: 18px;
  height: 18px;
`;

const CheckboxLabel = styled.label`
  font-size: 1rem;
  cursor: pointer;
`;

const DateInput = styled.input`
  width: 100%;
  height: 50px;
  padding: 5px 10px;
  font-size: 1rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 4px;

  &:focus {
    outline: none;
    border-color: #3d85f5;
  }
`;

const NoticeContainer = styled.div`
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid ${({ theme }) => theme.borderColor};
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const NoticeLabel = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 5px;
`;

const NoticeText = styled.div`
  font-size: 0.9rem;
  color: #333;
  line-height: 1.4;
`;

const NoticeDetailModal = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 200;
  width: 90%;
  max-width: 600px;
`;

const NoticeDetailContent = styled.div`
  background: white;
  padding: 25px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const NoticeDetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};

  h2 {
    font-size: 1.3rem;
    font-weight: 600;
    margin: 0;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 5px 10px;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const NoticeDetailDate = styled.div`
  font-size: 0.9rem;
  color: #666;
  margin-bottom: 20px;
`;

const NoticeDetailBody = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  white-space: pre-wrap;

  p {
    margin-bottom: 10px;
  }
`;
